export class InputManager {
  private keys = new Set<string>();
  private mouseButtons = new Set<number>();
  private keysJustPressed = new Set<string>();
  private mouseJustPressed = new Set<number>();
  isMobile: boolean;

  // Mouse movement delta (consumed each frame)
  mouseDeltaX = 0;
  mouseDeltaY = 0;

  // Touch joystick state
  touchMoveX = 0;
  touchMoveY = 0;

  // Touch buttons state
  touchFire = false;
  touchADS = false;
  touchJump = false;
  touchCrouch = false;
  touchSprint = false;
  touchReload = false;
  touchFlashlight = false;
  touchWeaponSwitch = false;
  touchInteract = false;

  private pointerLocked = false;

  // Touch tracking
  private joystickTouchId: number | null = null;
  private lookTouchId: number | null = null;
  private lastLookX = 0;
  private lastLookY = 0;
  private joystickCenter = { x: 0, y: 0 };
  private mobileOverlay: HTMLElement | null = null;

  constructor() {
    this.isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    window.addEventListener('keydown', (e) => {
      if (!this.keys.has(e.code)) {
        this.keysJustPressed.add(e.code);
      }
      this.keys.add(e.code);
      if (this.pointerLocked) {
        e.preventDefault();
      }
    });

    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
    });

    window.addEventListener('mousedown', (e) => {
      if (!this.mouseButtons.has(e.button)) {
        this.mouseJustPressed.add(e.button);
      }
      this.mouseButtons.add(e.button);
    });

    window.addEventListener('mouseup', (e) => {
      this.mouseButtons.delete(e.button);
    });

    window.addEventListener('mousemove', (e) => {
      if (this.pointerLocked) {
        this.mouseDeltaX += e.movementX;
        this.mouseDeltaY += e.movementY;
      }
    });

    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement !== null;
    });

    window.addEventListener('contextmenu', (e) => e.preventDefault());

    // Setup mobile controls if touch device
    if (this.isMobile) {
      this.setupMobileControls();
    }
  }

  // ==================== MOBILE CONTROLS ====================

  private setupMobileControls(): void {
    // Prevent scroll/zoom
    document.body.style.touchAction = 'none';
    document.body.style.overscrollBehavior = 'none';

    // Create mobile overlay
    this.mobileOverlay = document.createElement('div');
    this.mobileOverlay.id = 'mobile-controls';
    this.mobileOverlay.style.cssText = `
      position:fixed;top:0;left:0;width:100%;height:100%;z-index:40;
      pointer-events:none;display:none;
    `;

    this.mobileOverlay.innerHTML = `
      <!-- Left: Virtual joystick area -->
      <div id="joystick-zone" style="position:absolute;left:0;bottom:0;width:40%;height:50%;pointer-events:auto;touch-action:none;">
        <div id="joystick-base" style="position:absolute;left:20%;bottom:15%;width:100px;height:100px;
          border-radius:50%;border:2px solid rgba(255,255,255,0.2);background:rgba(255,255,255,0.05);
          display:flex;align-items:center;justify-content:center;">
          <div id="joystick-thumb" style="width:40px;height:40px;border-radius:50%;
            background:rgba(255,255,255,0.3);border:2px solid rgba(255,255,255,0.5);
            transition:none;"></div>
        </div>
      </div>

      <!-- Right: Look area (entire right side) -->
      <div id="look-zone" style="position:absolute;right:0;top:0;width:60%;height:60%;pointer-events:auto;touch-action:none;"></div>

      <!-- Right side buttons (vertical column) -->
      <!-- Interact (top) -->
      <div id="btn-interact" style="position:absolute;right:15px;bottom:52%;width:50px;height:50px;
        border-radius:50%;border:2px solid rgba(100,255,100,0.5);background:rgba(100,255,100,0.1);
        display:flex;align-items:center;justify-content:center;pointer-events:auto;touch-action:none;">
        <span style="color:rgba(100,255,100,0.7);font-size:0.7rem;">E</span>
      </div>

      <!-- Jump -->
      <div id="btn-jump" style="position:absolute;right:15px;bottom:42%;width:50px;height:50px;
        border-radius:50%;border:2px solid rgba(100,200,255,0.5);background:rgba(100,200,255,0.1);
        display:flex;align-items:center;justify-content:center;pointer-events:auto;touch-action:none;">
        <span style="color:rgba(100,200,255,0.7);font-size:0.7rem;">↑</span>
      </div>

      <!-- Fire (large, center-right) -->
      <div id="btn-fire" style="position:absolute;right:15px;bottom:24%;width:70px;height:70px;
        border-radius:50%;border:3px solid rgba(255,80,80,0.6);background:rgba(255,0,0,0.15);
        display:flex;align-items:center;justify-content:center;pointer-events:auto;touch-action:none;">
        <span style="color:rgba(255,100,100,0.9);font-size:0.8rem;font-weight:bold;">FIRE</span>
      </div>

      <!-- ADS (left of fire) -->
      <div id="btn-ads" style="position:absolute;right:90px;bottom:28%;width:50px;height:50px;
        border-radius:50%;border:2px solid rgba(200,200,255,0.5);background:rgba(150,150,255,0.1);
        display:flex;align-items:center;justify-content:center;pointer-events:auto;touch-action:none;">
        <span style="color:rgba(200,200,255,0.7);font-size:0.6rem;">ADS</span>
      </div>

      <!-- Reload (left of fire, below ADS) -->
      <div id="btn-reload" style="position:absolute;right:90px;bottom:16%;width:50px;height:50px;
        border-radius:50%;border:2px solid rgba(255,200,0,0.5);background:rgba(255,200,0,0.1);
        display:flex;align-items:center;justify-content:center;pointer-events:auto;touch-action:none;">
        <span style="color:rgba(255,200,0,0.7);font-size:0.7rem;">R</span>
      </div>

      <!-- Bottom bar: Sprint + Weapon switch (1/2/3) -->
      <div style="position:absolute;left:50%;bottom:6px;transform:translateX(-50%);display:flex;gap:6px;align-items:center;pointer-events:auto;">
        <div id="btn-sprint" style="width:42px;height:36px;border:2px solid rgba(255,165,0,0.4);background:rgba(255,165,0,0.1);
          border-radius:4px;display:flex;align-items:center;justify-content:center;touch-action:none;">
          <span style="color:rgba(255,165,0,0.7);font-size:0.65rem;">走</span>
        </div>
        <div class="btn-weapon-switch" data-slot="0" style="width:40px;height:36px;border:1px solid rgba(255,100,100,0.4);
          background:rgba(255,0,0,0.1);border-radius:4px;display:flex;align-items:center;justify-content:center;
          color:rgba(255,100,100,0.7);font-size:0.75rem;touch-action:none;">1</div>
        <div class="btn-weapon-switch" data-slot="1" style="width:40px;height:36px;border:1px solid rgba(100,100,255,0.4);
          background:rgba(0,0,255,0.1);border-radius:4px;display:flex;align-items:center;justify-content:center;
          color:rgba(100,100,255,0.7);font-size:0.75rem;touch-action:none;">2</div>
        <div class="btn-weapon-switch" data-slot="2" style="width:40px;height:36px;border:1px solid rgba(100,255,100,0.4);
          background:rgba(0,255,0,0.1);border-radius:4px;display:flex;align-items:center;justify-content:center;
          color:rgba(100,255,100,0.7);font-size:0.75rem;touch-action:none;">3</div>
      </div>
    `;

    document.body.appendChild(this.mobileOverlay);
    this.bindTouchEvents();
  }

  private bindTouchEvents(): void {
    // --- Joystick ---
    const joyZone = document.getElementById('joystick-zone')!;
    const joyBase = document.getElementById('joystick-base')!;
    const joyThumb = document.getElementById('joystick-thumb')!;

    joyZone.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      this.joystickTouchId = touch.identifier;
      const rect = joyBase.getBoundingClientRect();
      this.joystickCenter = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }, { passive: false });

    joyZone.addEventListener('touchmove', (e) => {
      e.preventDefault();
      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        if (touch.identifier === this.joystickTouchId) {
          const dx = touch.clientX - this.joystickCenter.x;
          const dy = touch.clientY - this.joystickCenter.y;
          const maxR = 45;
          const dist = Math.min(Math.sqrt(dx * dx + dy * dy), maxR);
          const angle = Math.atan2(dy, dx);
          const nx = Math.cos(angle) * dist;
          const ny = Math.sin(angle) * dist;
          joyThumb.style.transform = `translate(${nx}px, ${ny}px)`;
          this.touchMoveX = nx / maxR; // -1 to 1
          this.touchMoveY = ny / maxR;
        }
      }
    }, { passive: false });

    const endJoystick = (e: TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === this.joystickTouchId) {
          this.joystickTouchId = null;
          this.touchMoveX = 0;
          this.touchMoveY = 0;
          joyThumb.style.transform = '';
        }
      }
    };
    joyZone.addEventListener('touchend', endJoystick);
    joyZone.addEventListener('touchcancel', endJoystick);

    // --- Look (camera rotation) ---
    const lookZone = document.getElementById('look-zone')!;
    lookZone.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      this.lookTouchId = touch.identifier;
      this.lastLookX = touch.clientX;
      this.lastLookY = touch.clientY;
    }, { passive: false });

    lookZone.addEventListener('touchmove', (e) => {
      e.preventDefault();
      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        if (touch.identifier === this.lookTouchId) {
          this.mouseDeltaX += (touch.clientX - this.lastLookX) * 1.5;
          this.mouseDeltaY += (touch.clientY - this.lastLookY) * 1.5;
          this.lastLookX = touch.clientX;
          this.lastLookY = touch.clientY;
        }
      }
    }, { passive: false });

    const endLook = (e: TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === this.lookTouchId) {
          this.lookTouchId = null;
        }
      }
    };
    lookZone.addEventListener('touchend', endLook);
    lookZone.addEventListener('touchcancel', endLook);

    // --- Action buttons ---
    const bindButton = (id: string, onDown: () => void, onUp: () => void) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('touchstart', (e) => { e.preventDefault(); onDown(); }, { passive: false });
      el.addEventListener('touchend', (e) => { e.preventDefault(); onUp(); });
      el.addEventListener('touchcancel', () => onUp());
    };

    bindButton('btn-fire',
      () => { this.touchFire = true; this.mouseJustPressed.add(0); },
      () => { this.touchFire = false; }
    );
    bindButton('btn-reload',
      () => { this.keysJustPressed.add('KeyR'); this.keys.add('KeyR'); },
      () => { this.keys.delete('KeyR'); }
    );
    bindButton('btn-jump',
      () => { this.keysJustPressed.add('Space'); this.keys.add('Space'); },
      () => { this.keys.delete('Space'); }
    );
    bindButton('btn-interact',
      () => { this.keysJustPressed.add('KeyE'); this.keys.add('KeyE'); this.touchInteract = true; },
      () => { this.keys.delete('KeyE'); this.touchInteract = false; }
    );
    bindButton('btn-ads',
      () => { this.touchADS = true; },
      () => { this.touchADS = false; }
    );
    bindButton('btn-sprint',
      () => { this.touchSprint = !this.touchSprint; }, // toggle
      () => {}
    );

    // Weapon switch buttons
    document.querySelectorAll('.btn-weapon-switch').forEach(el => {
      el.addEventListener('touchstart', (e) => {
        e.preventDefault();
        const slot = (el as HTMLElement).getAttribute('data-slot');
        if (slot === '0') { this.keysJustPressed.add('Digit1'); this.keys.add('Digit1'); setTimeout(() => this.keys.delete('Digit1'), 100); }
        if (slot === '1') { this.keysJustPressed.add('Digit2'); this.keys.add('Digit2'); setTimeout(() => this.keys.delete('Digit2'), 100); }
        if (slot === '2') { this.keysJustPressed.add('Digit3'); this.keys.add('Digit3'); setTimeout(() => this.keys.delete('Digit3'), 100); }
      }, { passive: false });
    });
  }

  showMobileControls(): void {
    if (this.mobileOverlay) this.mobileOverlay.style.display = 'block';
  }

  hideMobileControls(): void {
    if (this.mobileOverlay) this.mobileOverlay.style.display = 'none';
  }

  // ==================== STANDARD API ====================

  requestPointerLock(element: HTMLElement): void {
    if (!this.isMobile) {
      element.requestPointerLock();
    }
  }

  isPointerLocked(): boolean {
    return this.pointerLocked || this.isMobile;
  }

  isKeyDown(code: string): boolean {
    return this.keys.has(code);
  }

  isKeyJustPressed(code: string): boolean {
    return this.keysJustPressed.has(code);
  }

  isMouseDown(button: number): boolean {
    return this.mouseButtons.has(button);
  }

  isMouseJustPressed(button: number): boolean {
    return this.mouseJustPressed.has(button);
  }

  getKeys(): Set<string> {
    return this.keys;
  }

  isFiring(): boolean {
    return this.mouseButtons.has(0) || this.touchFire;
  }

  isADS(): boolean {
    return this.mouseButtons.has(2) || this.touchADS;
  }

  consumeMouseDelta(): { x: number; y: number } {
    const delta = { x: this.mouseDeltaX, y: this.mouseDeltaY };
    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;
    return delta;
  }

  endFrame(): void {
    this.keysJustPressed.clear();
    this.mouseJustPressed.clear();
  }
}
